import { useEffect, useEffectEvent, useState } from 'react'
import { useLocation } from 'react-router'
import { useCartDrawer } from 'features/cart/components/CartDrawerProvider'
import { useBagAnalytics } from 'features/cart/hooks/useBagAnalytics'
import { EVENT_NAMES } from 'services/Analytics/Analytics'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { CART_UPSELLS } from '../cartUpsells.registry'
import {
  type CartUpsellId,
  type CartUpsellOffer,
  type CartUpsellRecords,
} from '../cartUpsells.schemas'
import type { CartUpsellCardData } from '../cartUpsells.types'
import {
  getCartUpsellOffer,
  markUpsellDismissed,
  useCartUpsellRecords,
} from '../cartUpsellsStorage'
import { useOfferedCartUpsells } from '../hooks/useOfferedCartUpsells'
import { CartUpsellCarousel } from './CartUpsellCarousel'
import { CartUpsellFlow } from './CartUpsellFlow'
import * as S from './CartUpsellsSection.styles'

// The offer is captured on tap: applying it retires the card while the Flow's sheet is still animating out.
type OpenedCartUpsellFlow = {
  id: CartUpsellId
  offer: CartUpsellOffer<CartUpsellId>
  isOpen: boolean
}

function getOpenedCartUpsellFlow(
  id: CartUpsellId,
  records: CartUpsellRecords
): OpenedCartUpsellFlow {
  const offer = getCartUpsellOffer(records[id])
  if (!offer) throw new Error(`Cart upsell ${id} has no offer to open`)
  return { id, offer, isOpen: true }
}

type CartUpsellsSectionProps = {
  onUpsellAddedToBag: () => void
}

export function CartUpsellsSection({
  onUpsellAddedToBag,
}: CartUpsellsSectionProps) {
  const offeredUpsells = useOfferedCartUpsells()
  const experimentManager = useExperimentManager()
  const shownUpsells = offeredUpsells.filter((card) =>
    experimentManager.isEnabled(CART_UPSELLS[card.id].experimentFlag)
  )
  const location = useLocation()
  // The bag stays open across navigation, so keying by path is what resets an open Flow on a route change.
  return (
    <CartUpsells
      key={location.pathname}
      shownUpsells={shownUpsells}
      onUpsellAddedToBag={onUpsellAddedToBag}
    />
  )
}

function CartUpsells({
  shownUpsells,
  onUpsellAddedToBag,
}: CartUpsellsSectionProps & { shownUpsells: CartUpsellCardData[] }) {
  const records = useCartUpsellRecords()
  const [openedFlow, setOpenedFlow] = useState<OpenedCartUpsellFlow | null>(
    null
  )
  const { trackBagEvent } = useBagAnalytics()
  const onCardClick = (card: CartUpsellCardData, position: number) => {
    trackBagEvent(EVENT_NAMES.CART_UPSELL_CARD_TAPPED, {
      ...card.analyticsProps,
      'Card Position': position,
    })
    setOpenedFlow(getOpenedCartUpsellFlow(card.id, records))
  }
  const onCardDismiss = (card: CartUpsellCardData, position: number) => {
    trackBagEvent(EVENT_NAMES.CART_UPSELL_CARD_DISMISSED, {
      ...card.analyticsProps,
      'Card Position': position,
    })
    markUpsellDismissed(card.id)
  }
  return (
    <>
      {shownUpsells.length > 0 && (
        <CartUpsellCards
          cards={shownUpsells}
          onCardClick={onCardClick}
          onCardDismiss={onCardDismiss}
        />
      )}
      {openedFlow && (
        <CartUpsellFlow
          key={openedFlow.id}
          {...openedFlow}
          onClose={() => setOpenedFlow({ ...openedFlow, isOpen: false })}
          onAddedToBag={() => {
            setOpenedFlow({ ...openedFlow, isOpen: false })
            onUpsellAddedToBag()
          }}
          onExited={() => setOpenedFlow(null)}
        />
      )}
    </>
  )
}

function CartUpsellCards({
  cards,
  onCardClick,
  onCardDismiss,
}: {
  cards: CartUpsellCardData[]
  onCardClick: (card: CartUpsellCardData, position: number) => void
  onCardDismiss: (card: CartUpsellCardData, position: number) => void
}) {
  const { trackBagEvent } = useBagAnalytics()
  const { isCartOpen } = useCartDrawer()
  const trackUpsellsAvailable = useEffectEvent(() => {
    // The bag's Edit and Add Product navigate after closing it, remounting the cards during its exit animation.
    if (!isCartOpen) return
    trackBagEvent(EVENT_NAMES.CART_UPSELLS_AVAILABLE, {
      'Upsell IDs': cards.map((card) => card.id),
      Count: cards.length,
    })
  })
  useEffect(() => {
    trackUpsellsAvailable()
  }, [])
  return (
    <S.Section data-testid="cart-upsells">
      <S.Panel>
        <CartUpsellCarousel
          cards={cards}
          onCardClick={onCardClick}
          onCardDismiss={onCardDismiss}
        />
      </S.Panel>
    </S.Section>
  )
}
